import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { BoolEmployeePref } from 'src/generated/mloop-graphql';

import { useEmployeePrefBool } from 'src/hooks/api/employee/get';
import { useSetEmployeePrefBool } from 'src/hooks/api/employee/set';
import useEmployeeId from 'src/hooks/useEmployeeId';

type TaskTimelineVisibilityContextValue = {
  /** False until the stored preference resolves, so the timeline never flashes open and then closed. */
  resolved: boolean;
  timelineVisible: boolean;
  setTimelineVisible: (visible: boolean) => void;
};

const defaultContextValue: TaskTimelineVisibilityContextValue = {
  resolved: false,
  timelineVisible: true,
  setTimelineVisible: () => {},
};

const TaskTimelineVisibilityContext = createContext<TaskTimelineVisibilityContextValue>(defaultContextValue);

type TaskTimelineVisibilityProviderProps = {
  children?: ReactNode;
};

/**
 * Holds whether the task timeline is visible, so every surface that reacts to it — the task view
 * itself and the side panel width that hosts it — reads one value and cannot disagree.
 *
 * The timeline is visible until a user hides it, and stays hidden for that user across tasks and
 * sessions through the `bool_hide_task_timeline` employee preference.
 */
export const TaskTimelineVisibilityProvider: FC<TaskTimelineVisibilityProviderProps> = ({ children }) => {
  const employeeId = useEmployeeId();
  const [timelineHidden, { loading }] = useEmployeePrefBool(
    BoolEmployeePref.BoolHideTaskTimeline,
    employeeId as string
  );
  const setTimelineHidden = useSetEmployeePrefBool(BoolEmployeePref.BoolHideTaskTimeline, employeeId as string);

  // The stored preference refetches before it reflects a write, so the in-session choice wins.
  const [pendingVisible, setPendingVisible] = useState<boolean | null>(null);

  const setTimelineVisible = useCallback(
    (visible: boolean) => {
      setPendingVisible(visible);
      void setTimelineHidden(!visible);
    },
    [setTimelineHidden]
  );

  const value = useMemo<TaskTimelineVisibilityContextValue>(
    () => ({
      resolved: Boolean(employeeId) && !loading,
      timelineVisible: pendingVisible ?? !timelineHidden,
      setTimelineVisible,
    }),
    [employeeId, loading, pendingVisible, setTimelineVisible, timelineHidden]
  );

  return <TaskTimelineVisibilityContext.Provider value={value}>{children}</TaskTimelineVisibilityContext.Provider>;
};

/**
 * Reads and writes whether the task timeline is visible for the current user.
 */
export const useTaskTimelineVisibility = (): TaskTimelineVisibilityContextValue => {
  return useContext(TaskTimelineVisibilityContext);
};
